import { FilterCategory } from "../models/filter";
import { Location, LocationSummary } from "../models/location";
import { Order, OrderStatus } from "../models/order";
import { SidebarType } from "../models/sidebar";
import { SortType } from "../models/sort";
import { User } from "../models/user";
import { ViewType } from "../models/view";

export interface State {
  selectedLocation?: Location;
  locations: LocationSummary[];
  filters: ViewFilterConfig;
  orders: OrderState;
  pinnedOrders: Order[];
  sidebar: SidebarState;
  user?: User;
  viewType: ViewType;
  settings: SettingsState;
}

export type ViewFilterConfig = {
  [key in ViewType]: FilterConfig;
};

export type FilterConfig = {
  [key in FilterCategory]?: string[];
};

export interface OrderState {
  loading: boolean;
  error?: Error;
  orders?: Order[];
}

// Orders are refetched in full on an interval, so `loading` alone must never
// decide whether to render a placeholder instead of the orders — that tears the
// orders table down and rebuilds it on every refetch. Orders are only genuinely
// unavailable until the first response arrives.
export function isInitialOrderLoad({ loading, orders }: OrderState): boolean {
  return orders == null || (loading && orders.length === 0);
}

export interface SidebarState {
  isOpen: boolean;
  type?: SidebarType;
}

// Settings the operator can change in the UI. Only these are persisted per
// user — see UserPreferences.
export interface PersistedSettings {
  customerViewType: CustomerViewType;
  customerViewTableCount: number;
  customerViewFontSize: number;
  customerViewOrdersPerTable: number;
  autoClearTimeouts: AutoClearTimeouts;
  sortType: SortType;
}

// The persisted settings plus tuning constants that have no UI. Those must
// always come from initialState rather than from a stored copy, or changing one
// never reaches a browser that has already saved preferences.
export interface SettingsState extends PersistedSettings {
  customerViewPagingIntervalInMS: number;
  orderRefetchIntervalInMS: number;
  statusTimeoutTickIntervalInMS: number;
}

export interface AutoClearTimeouts {
  [OrderStatus.Ready]: number;
  [OrderStatus.CheckIn]: number;
  [OrderStatus.Error]: number;
}

export enum CustomerViewType {
  COLUMN = "COLUMN",
  GRID = "GRID",
}

export const initialState: State = {
  selectedLocation: undefined,
  locations: [],
  filters: {
    [ViewType.CUSTOMER]: {},
    [ViewType.RESTAURANT]: {},
  },
  orders: {
    loading: true,
    orders: [],
  },
  pinnedOrders: [],
  sidebar: {
    isOpen: false,
    type: SidebarType.MENU,
  },
  viewType: ViewType.RESTAURANT,
  settings: {
    customerViewType: CustomerViewType.COLUMN,
    customerViewTableCount: 1,
    customerViewFontSize: 1,
    customerViewOrdersPerTable: 10,
    customerViewPagingIntervalInMS: 5000,
    // The poll is the only source of order updates while GraphQL.useSubscriptions
    // is off, so it bounds how stale the table can get.
    orderRefetchIntervalInMS: 5000,
    // autoClearTimeouts are measured against the wall clock, so the views
    // re-apply them on this interval as well as when order data changes. It
    // bounds how long past its timeout an order can linger on screen.
    statusTimeoutTickIntervalInMS: 5000,
    autoClearTimeouts: {
      [OrderStatus.Ready]: 5,
      [OrderStatus.CheckIn]: 15,
      [OrderStatus.Error]: 15,
    },
    sortType: SortType.OLDEST_AT_TOP,
  },
};
